import { BackgroundCheckStatus, BackgroundCheckType } from '@/backend/features/background-check/types';
import { ExtendedAccountData } from '@/backend/features/contact-management/entities/extended-account/types';
import { ProfileBackgroundCheckCard, SlideoutBackgroundCheckCard } from '@/ui/features/accounts';
import { useSelectedPersonBackgroundCheckData } from '@/ui/hooks/useSelectedPersonBackgroundCheckData';
import { LicenseStatus } from '@juvare/credentialing/backend/extraTypes';
import {
    ProfileOigVerificationCard,
    ProfileOigVerificationCardProps,
    SlideoutOigVerificationCard,
} from '@juvare/credentialing/ui/features/credentialing-verification';
import { getSettings } from '@juvare/platform-sdk-react/ui/features/editable-settings';
import { Box, Grid } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

export interface AdditionAccountHeaderContentProps extends ProfileOigVerificationCardProps {
    accountId: string;
    account: ExtendedAccountData;
    cardStyle?: 'profile' | 'slideout';
}

export const AdditionAccountHeaderContent: React.FC<AdditionAccountHeaderContentProps> = ({ accountId, account, cardStyle }) => {
    const { t } = useTranslation('rms');
    const isSlideout = cardStyle === 'slideout';
    const OigVerificationCard = isSlideout ? SlideoutOigVerificationCard : ProfileOigVerificationCard;
    const BackgroundCheckCard = isSlideout ? SlideoutBackgroundCheckCard : ProfileBackgroundCheckCard;

    const { backgroundCheckConfig } = useSelector(getSettings);

    const activeBackgroundCheckTypes = Object.entries(backgroundCheckConfig.type)
        .map(([type, value]) => {
            if (value.enabled) {
                return type;
            }

            return;
        })
        .filter((value) => !!value);

    const { backgroundCheckData } = useSelectedPersonBackgroundCheckData(accountId, [
        {
            column: 'type',
            op: 'in',
            value: activeBackgroundCheckTypes,
        },
    ]);

    return (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%', mt: { xs: 3, sm: 0 } }}>
            <Grid container spacing={1} sx={{ maxWidth: { sm: isSlideout ? undefined : '308px' }, ml: { sm: isSlideout ? 0 : 3 } }}>
                <Grid item xs={12}>
                    <OigVerificationCard oigStatus={account.oigStatus as LicenseStatus} />
                </Grid>
                {backgroundCheckData?.map((data) => (
                    <Grid key={data.id} item xs={12}>
                        <BackgroundCheckCard
                            cardTitle={
                                activeBackgroundCheckTypes.length > 1
                                    ? t('credentialing.backgroundCheckType', {
                                          type: backgroundCheckConfig.type[data.columns.type as BackgroundCheckType]?.label,
                                      })
                                    : undefined
                            }
                            backgroundCheckStatus={data.columns.status as BackgroundCheckStatus}
                        />
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
};
